// PluginRegistry.ts

// 🔧 GLOBAL TYPE DECLARATIONS
declare global {
  interface Window {
    __LEXICAL_NODE_REGISTRY?: Record<string, boolean>;
  }
}

export interface LexicalPluginContract {
  id: string;
  nodeTypes: string[];
  register(): void;
  isReady(): boolean;
  teardown?(): void;
}

const pluginRegistry: Record<string, LexicalPluginContract> = {};

export function registerPlugin(plugin: LexicalPluginContract) {
  pluginRegistry[plugin.id] = plugin;
  plugin.register();
  plugin.nodeTypes.forEach((type) => {
    if (!window.__LEXICAL_NODE_REGISTRY) {
      window.__LEXICAL_NODE_REGISTRY = {};
    }
    window.__LEXICAL_NODE_REGISTRY[type] = true;
  });
}

export function allPluginsReady(): boolean {
  return Object.values(pluginRegistry).every((plugin) => plugin.isReady());
}

export function getRegisteredNodeTypes(): string[] {
  return Object.keys(window.__LEXICAL_NODE_REGISTRY || {});
}

// 🛡️ HYDRATION SAFETY GUARD - Comprehensive state validation before hydration

export interface HydrationValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  sanitizedState?: any;
  unregisteredNodes: string[];
  structuralIssues: string[];
}

export function validateEditorStateForHydration(raw: string): HydrationValidationResult {
  const result: HydrationValidationResult = {
    isValid: false,
    errors: [],
    warnings: [],
    unregisteredNodes: [],
    structuralIssues: []
  };

  try {
    console.log('🛡️ [HydrationGuard] Starting comprehensive state validation...');
    
    // Step 1: JSON Parse Validation
    let parsed: any;
    try {
      parsed = JSON.parse(raw);
    } catch (parseError) {
      result.errors.push(`JSON parsing failed: ${parseError instanceof Error ? parseError.message : String(parseError)}`);
      return result;
    }

    // Step 2: Root Structure Validation
    if (!parsed.root) {
      result.structuralIssues.push('Missing root node');
      return result;
    }

    if (parsed.root.type !== 'root') {
      result.structuralIssues.push(`Invalid root type: ${parsed.root.type}`);
      return result;
    }

    if (!Array.isArray(parsed.root.children)) {
      result.structuralIssues.push('Root children is not an array');
      return result;
    }

    // Step 3: Deep Node Type Discovery & Validation
    const foundTypes = new Set<string>();
    const nodeValidationErrors: string[] = [];

    const traverseAndValidate = (node: any, path: string = 'root') => {
      // Validate node structure
      if (!node || typeof node !== 'object') {
        nodeValidationErrors.push(`Invalid node at ${path}: not an object`);
        return;
      }

      if (!node.type || typeof node.type !== 'string') {
        nodeValidationErrors.push(`Invalid node at ${path}: missing or invalid type`);
        return;
      }

      foundTypes.add(node.type);

      // Recursively validate children
      if (node.children) {
        if (!Array.isArray(node.children)) {
          nodeValidationErrors.push(`Invalid children at ${path}: not an array`);
          return;
        }

        node.children.forEach((child: any, index: number) => {
          traverseAndValidate(child, `${path}.children[${index}]`);
        });
      }
    };

    traverseAndValidate(parsed.root);

    // Add node validation errors to structural issues
    result.structuralIssues.push(...nodeValidationErrors);

    // Step 4: Plugin Registry Validation
    const registeredTypes = getRegisteredNodeTypes();
    const unregisteredTypes = [...foundTypes].filter(
      type => !registeredTypes.includes(type)
    );

    result.unregisteredNodes = unregisteredTypes;

    if (unregisteredTypes.length > 0) {
      result.errors.push(`Unregistered node types found: ${unregisteredTypes.join(', ')}`);
      console.warn('🛡️ [HydrationGuard] Unregistered node types:', unregisteredTypes);
      console.warn('🛡️ [HydrationGuard] Registered types:', registeredTypes);
    }

    // Step 5: Plugin Readiness Check
    if (!allPluginsReady()) {
      result.errors.push('Not all plugins are ready for hydration');
    }

    // Step 6: JSON Restringification Test (catches circular references, etc.)
    try {
      JSON.stringify(parsed);
    } catch (stringifyError) {
      result.structuralIssues.push(`JSON restringification failed: ${stringifyError instanceof Error ? stringifyError.message : String(stringifyError)}`);
    }

    // Step 7: Final Validation Assessment
    const hasErrors = result.errors.length > 0 || result.structuralIssues.length > 0;
    result.isValid = !hasErrors;

    if (result.isValid) {
      result.sanitizedState = parsed;
      console.log('✅ [HydrationGuard] State validation PASSED - safe for hydration');
    } else {
      console.error('🚨 [HydrationGuard] State validation FAILED:', {
        errors: result.errors,
        structuralIssues: result.structuralIssues,
        unregisteredNodes: result.unregisteredNodes
      });
    }

    return result;
  } catch (unexpectedError) {
    result.errors.push(`Unexpected validation error: ${unexpectedError instanceof Error ? unexpectedError.message : String(unexpectedError)}`);
    console.error('🚨 [HydrationGuard] Unexpected validation error:', unexpectedError);
    return result;
  }
}

// 🔄 BACKWARD COMPATIBILITY: Enhanced getSafeEditorState using new validation
export function getSafeEditorState(raw: string): any | undefined {
  if (!raw || typeof raw !== 'string' || raw.trim() === '') {
    return undefined;
  }

  const validation = validateEditorStateForHydration(raw);
  
  if (validation.isValid && validation.sanitizedState) {
    console.log('✅ [getSafeEditorState] Using validated state for hydration');
    return JSON.stringify(validation.sanitizedState);
  }

  console.warn('🚨 [getSafeEditorState] State validation failed - returning undefined for fallback');
  return undefined;
}

// 📝 EXAMPLE PLUGIN IMPLEMENTATIONS

// Step 5: Each plugin must self-register its node types
export const VersionNodePlugin: LexicalPluginContract = {
  id: 'VersionNode',
  nodeTypes: ['VERSION'],
  register: () => {
    console.log('🔌 [VersionNodePlugin] Registering VERSION node type...');
    // In a real implementation, you would:
    // import { VersionNode } from './nodes/VersionNode';
    // editor.registerNode('VERSION', VersionNode);
    
    // For demo purposes, we'll just mark it as registered
    if (!window.__LEXICAL_NODE_REGISTRY) {
      window.__LEXICAL_NODE_REGISTRY = {};
    }
    window.__LEXICAL_NODE_REGISTRY['VERSION'] = true;
    console.log('✅ [VersionNodePlugin] VERSION node type registered successfully');
  },
  isReady: () => {
    const isReady = !!window.__LEXICAL_NODE_REGISTRY?.VERSION;
    console.log(`🔌 [VersionNodePlugin] Readiness check: ${isReady ? 'READY' : 'NOT READY'}`);
    return isReady;
  },
  teardown: () => {
    console.log('🔌 [VersionNodePlugin] Tearing down VERSION node type...');
    if (window.__LEXICAL_NODE_REGISTRY) {
      delete window.__LEXICAL_NODE_REGISTRY['VERSION'];
    }
  }
};

// Core Lexical Nodes Plugin (always required)
export const CoreNodesPlugin: LexicalPluginContract = {
  id: 'CoreNodes',
  nodeTypes: ['root', 'paragraph', 'text', 'heading', 'list', 'listitem', 'quote', 'link', 'autolink', 'table', 'tablecell', 'tablerow', 'code'],
  register: () => {
    console.log('🔌 [CoreNodesPlugin] Registering core Lexical node types...');
    const coreTypes = ['root', 'paragraph', 'text', 'heading', 'list', 'listitem', 'quote', 'link', 'autolink', 'table', 'tablecell', 'tablerow', 'code'];
    
    if (!window.__LEXICAL_NODE_REGISTRY) {
      window.__LEXICAL_NODE_REGISTRY = {};
    }
    
    coreTypes.forEach(type => {
      if (window.__LEXICAL_NODE_REGISTRY) {
        window.__LEXICAL_NODE_REGISTRY[type] = true;
      }
    });
    
    console.log('✅ [CoreNodesPlugin] All core node types registered successfully');
  },
  isReady: () => {
    const coreTypes = ['root', 'paragraph', 'text', 'heading', 'list', 'listitem', 'quote', 'link', 'autolink', 'table', 'tablecell', 'tablerow', 'code'];
    const registry = window.__LEXICAL_NODE_REGISTRY;
    const allReady = registry ? coreTypes.every(type => !!registry[type]) : false;
    console.log(`🔌 [CoreNodesPlugin] Readiness check: ${allReady ? 'READY' : 'NOT READY'}`);
    return allReady;
  }
};

// 🚀 STEP 6: FINAL INTEGRATION INSTRUCTIONS
// Safe Editor Initialization Pattern

export interface EditorInitializationResult {
  success: boolean;
  state?: any;
  fallbackReason?: string;
  validationResult?: HydrationValidationResult;
}

export async function initializeEditorSafely(savedState: string): Promise<EditorInitializationResult> {
  console.log('🚀 [EditorInit] Starting safe editor initialization...');
  
  try {
    // Step 1: Register all required plugins
    console.log('🚀 [EditorInit] Step 1: Registering plugins...');
    registerPlugin(CoreNodesPlugin);
    // registerPlugin(VersionNodePlugin); // Only if VERSION nodes are expected
    
    // Step 2: Wait for all plugins to be ready
    console.log('🚀 [EditorInit] Step 2: Checking plugin readiness...');
    const pluginsReady = allPluginsReady();
    if (!pluginsReady) {
      return {
        success: false,
        fallbackReason: 'Plugins not ready for hydration'
      };
    }
    
    // Step 3: Validate editor state for hydration safety
    console.log('🚀 [EditorInit] Step 3: Validating editor state...');
    const validationResult = validateEditorStateForHydration(savedState);
    
    if (validationResult.isValid && validationResult.sanitizedState) {
      console.log('✅ [EditorInit] Initialization successful - state ready for hydration');
      return {
        success: true,
        state: validationResult.sanitizedState,
        validationResult
      };
    } else {
      console.warn('🚨 [EditorInit] State validation failed - falling back to plain text mode');
      return {
        success: false,
        fallbackReason: `Validation failed: ${validationResult.errors.join(', ')}`,
        validationResult
      };
    }
    
  } catch (error) {
    console.error('🚨 [EditorInit] Unexpected initialization error:', error);
    return {
      success: false,
      fallbackReason: `Initialization error: ${error instanceof Error ? error.message : String(error)}`
    };
  }
}

// 📋 USAGE EXAMPLE:
/*
// At app startup:
const initResult = await initializeEditorSafely(savedDocumentState);

if (initResult.success) {
  // Safe to hydrate with initResult.state
  const editorConfig = {
    editorState: JSON.stringify(initResult.state),
    // ... other config
  };
} else {
  // Fallback to plain text mode or show error
  console.warn('Falling back to plain text:', initResult.fallbackReason);
  const editorConfig = {
    editorState: undefined, // Empty editor
    // ... other config
  };
}
*/

// 🧪 DEBUGGING UTILITIES
export function debugPluginRegistry(): void {
  console.log('🔍 [DebugRegistry] Current plugin registry state:');
  console.log('Registered plugins:', Object.keys(pluginRegistry));
  console.log('Plugin readiness:', Object.fromEntries(
    Object.entries(pluginRegistry).map(([id, plugin]) => [id, plugin.isReady()])
  ));
  console.log('Registered node types:', getRegisteredNodeTypes());
  console.log('All plugins ready:', allPluginsReady());
}
